/**
 * 使い方ガイドの手順（純粋関数だけ）
 * ガイドの文言・順番・立ち寄る画面・指し示す目印は、すべてこのファイルの STEP_DEFINITIONS にあります。
 * 公開設定（管理画面で編集できる歓迎文・交換場所・準備中フラグ）から、表示する手順の並び（plan）を毎回導出します。
 * 今どの手順かは VisitorGuidedTour.tsx のstoreが手順IDだけで持ち、ここでは保持しません。
 */

import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import type { PublicAppSettings } from '../../../shared/config/appPublicSettings';
import type { GuidedTourAnchorId } from '../../../shared/components/guidedTourAnchor';

/** ガイドが立ち寄る画面（下部タブの4画面だけ） */
export type GuidedTourStopRoute =
  | typeof VISITOR_ROUTES.HOME
  | typeof VISITOR_ROUTES.SCAN
  | typeof VISITOR_ROUTES.REWARDS
  | typeof VISITOR_ROUTES.SURVEYS;

/** 立ち寄り先の表示名と下部タブの目印。表示名は VisitorBottomSwitchBar のタブの文言と同じにする（テストで確認） */
export const GUIDED_TOUR_STOP_TABS: Readonly<Record<GuidedTourStopRoute, Readonly<{ label: string; tabAnchor: GuidedTourAnchorId }>>> =
  Object.freeze({
    [VISITOR_ROUTES.HOME]: { label: 'ホーム', tabAnchor: 'tab.home' },
    [VISITOR_ROUTES.SCAN]: { label: 'QRスキャン', tabAnchor: 'tab.scan' },
    [VISITOR_ROUTES.REWARDS]: { label: '景品', tabAnchor: 'tab.rewards' },
    [VISITOR_ROUTES.SURVEYS]: { label: 'アンケート', tabAnchor: 'tab.surveys' },
  });

/**
 * ここへ移ったらガイドを終える画面
 * セッションが切れてログインし直す状態では、ガイドを続けても指す画面が無いため。
 * それ以外の立ち寄り先でない画面（企画一覧・お知らせ・入口など）は「寄り道」として一時停止する。
 */
const ABANDON_ROUTES: ReadonlySet<string> = new Set([VISITOR_ROUTES.LOGIN, VISITOR_ROUTES.REGISTER]);

/** 1手順の文言 */
type StepCopy = Readonly<{ title: string; body: string; hint: string | null }>;

/** 手順の定義 */
type StepDefinition = Readonly<{
  id: string;
  route: GuidedTourStopRoute;
  /** 指し示す目印。null は指し示さない */
  anchor: GuidedTourAnchorId | null;
  /** 目印の要素が画面に描画される設定か。描画されない設定では指し示さない */
  anchorVisible?: (settings: PublicAppSettings) => boolean;
  /** 明るく抜いた部分のタップを実際の要素へ通すか。押すと説明のModalが開き、ガイドは一時停止する */
  tapThrough: boolean;
  /** この設定で手順を表示するか。準備中の画面へは立ち寄らない */
  include: (settings: PublicAppSettings) => boolean;
  /** 文言。見出しは20字、本文は64字以内（320px幅・文字1.3倍で読める長さ。歓迎文は管理画面の設定なので除く） */
  copy: (settings: PublicAppSettings) => StepCopy;
}>;

/**
 * 常に表示する
 * @returns {boolean} true
 */
const ALWAYS = (): boolean => true;

/**
 * 手順の正本。この並びが表示順であり、手順が並びから消えたときに次の手順を探す順でもある。
 * 先頭の welcome と末尾の finish（ホームへ戻って終わる）は常に表示する。
 */
const STEP_DEFINITIONS = [
  {
    id: 'welcome',
    route: VISITOR_ROUTES.HOME,
    anchor: null,
    tapThrough: false,
    include: ALWAYS,
    copy: (settings) => ({
      title: settings.tutorialWelcomeTitle,
      body: settings.tutorialWelcomeBody,
      hint: '実際の画面を順番に案内します。「スキップ」でいつでも終われます。',
    }),
  },
  {
    id: 'home-points',
    route: VISITOR_ROUTES.HOME,
    anchor: 'home.points',
    tapThrough: false,
    include: ALWAYS,
    copy: () => ({
      title: '所持pt（ポイント）',
      body: '企画に参加するとここのptがたまり、勇者ランクが上がります。',
      hint: null,
    }),
  },
  {
    id: 'home-stamps',
    route: VISITOR_ROUTES.HOME,
    anchor: 'home.stampBook',
    tapThrough: false,
    include: (settings) => !settings.homeFeatureVisibility.eventsPreparing,
    copy: () => ({
      title: '5つの島のスタンプ帳',
      body: '各島の企画に参加するとスタンプが押されます。島をタップすると、その島の企画を探せます。',
      hint: null,
    }),
  },
  {
    id: 'home-help',
    route: VISITOR_ROUTES.HOME,
    anchor: 'screen.help',
    tapThrough: true,
    include: ALWAYS,
    copy: () => ({
      title: '困ったら「？」',
      // 相談先（B館前テント）は「？」の説明の「困ったときは」に出るため、補足欄は使わずカードを低く保つ
      body: 'どの画面でも右上の「？」で、ページの説明と文字の大きさの変更、困ったときの相談先を確認できます。押して試せます。',
      hint: null,
    }),
  },
  {
    id: 'scan-camera',
    route: VISITOR_ROUTES.SCAN,
    anchor: 'scan.camera',
    tapThrough: false,
    include: ALWAYS,
    copy: () => ({
      title: 'QRを読み取ってpt獲得',
      body: '企画のQRはptが自動で加算され、屋台のQRは評価画面が開きます。ガイド中はカメラを起動しません。',
      hint: null,
    }),
  },
  {
    id: 'scan-manual',
    route: VISITOR_ROUTES.SCAN,
    anchor: 'scan.manualCode',
    tapThrough: false,
    include: ALWAYS,
    copy: () => ({
      title: 'カメラが使えないときは',
      body: 'QRと一緒に表示されたコードを、この「ポイントコード（手入力）」へ入力してください。',
      hint: null,
    }),
  },
  {
    id: 'rewards-catalog',
    route: VISITOR_ROUTES.REWARDS,
    anchor: 'rewards.catalog',
    tapThrough: false,
    include: (settings) => !settings.homeFeatureVisibility.rewardsPreparing,
    // 事前申請の受付停止中かで交換の流れが変わる。カートは景品を入れるまで出ないため、別の手順にせずここで案内する
    copy: (settings) => ({
      title: 'ptで景品と交換',
      body: settings.homeFeatureVisibility.exchangeApplicationsPaused
        ? `${settings.exchangePlaceLabel}の列に並んでいる間に、景品をカートに入れて「注文してQRを発行」を押し、窓口で交換QRを見せます。`
        : `景品をカートに入れ、画面下のカートから事前申請するか、${settings.exchangePlaceLabel}で交換QRを発行して見せます。`,
      hint: null,
    }),
  },
  {
    id: 'rewards-history',
    route: VISITOR_ROUTES.REWARDS,
    anchor: 'rewards.history',
    tapThrough: false,
    include: (settings) => !settings.homeFeatureVisibility.rewardsPreparing,
    copy: () => ({
      title: '交換履歴',
      body: '申請・交換した景品は、この「交換履歴」で確認できます。',
      hint: null,
    }),
  },
  {
    id: 'surveys',
    route: VISITOR_ROUTES.SURVEYS,
    anchor: 'surveys.list',
    tapThrough: false,
    include: (settings) => !settings.homeFeatureVisibility.surveysPreparing,
    copy: () => ({
      title: 'アンケートでボーナスpt',
      body: '回答後に表示されるコードを、QRスキャン画面の「ポイントコード（手入力）」へ入力するとptがもらえます。',
      hint: null,
    }),
  },
  {
    id: 'finish',
    route: VISITOR_ROUTES.HOME,
    anchor: 'home.guideMenu',
    // 冒険メニューの「使い方ガイド」カードは homeFeatureVisibility.tutorial のときだけ描画される
    anchorVisible: (settings) => settings.homeFeatureVisibility.tutorial,
    tapThrough: false,
    include: ALWAYS,
    copy: (settings) => ({
      title: '冒険の準備ができました！',
      body: settings.homeFeatureVisibility.tutorial
        ? 'このガイドは、冒険メニューの「使い方ガイド」からいつでも見返せます。'
        : 'それでは冒険を楽しんでください！',
      hint: null,
    }),
  },
] as const satisfies readonly StepDefinition[];

/** 手順ID（STEP_DEFINITIONS から導出する） */
export type GuidedTourStepId = (typeof STEP_DEFINITIONS)[number]['id'];

/** 先頭の手順ID（開始時に使う） */
export const GUIDED_TOUR_FIRST_STEP_ID: GuidedTourStepId = 'welcome';

/**
 * 手順・ガイド本体が参照しうる目印IDの一覧（目印の契約テストで、ソースの tourAnchor('…') と突き合わせる）
 */
export const GUIDED_TOUR_REFERENCED_ANCHOR_IDS: readonly GuidedTourAnchorId[] = Object.freeze([
  'tabBar',
  ...Object.values(GUIDED_TOUR_STOP_TABS).map((stopTab) => stopTab.tabAnchor),
  ...STEP_DEFINITIONS.flatMap((definition) => (definition.anchor ? [definition.anchor] : [])),
]);

/** 進捗の目盛りの中での位置 */
export type GuidedTourProgress =
  | Readonly<{ kind: 'intro' }>
  | Readonly<{ kind: 'stop'; stopIndex: number }>
  | Readonly<{ kind: 'goal' }>;

/** 進捗の目盛り1つ（立ち寄る画面1つ） */
export type GuidedTourStop = Readonly<{ route: GuidedTourStopRoute; label: string }>;

/** カードがそのまま描画できる、解決済みの1手順 */
export type GuidedTourStep = Readonly<{
  id: GuidedTourStepId;
  route: GuidedTourStopRoute;
  anchor: GuidedTourAnchorId | null;
  tapThrough: boolean;
  /** カード上部の帯。'WELCOME' / 'ホーム 2/3' / 'GOAL' */
  badge: string;
  progress: GuidedTourProgress;
  title: string;
  body: string;
  hint: string | null;
  /** 次へボタンの文言。'次へ →' / 'QRスキャンへ →'（画面が変わるとき） / '⚔️ 冒険をはじめる！'（最後） */
  nextLabel: string;
  /** 次の手順が別の画面なら、その画面の下部タブの目印（来場者に押してもらう） */
  nextTabAnchor: GuidedTourAnchorId | null;
  /** 次の手順が別の画面なら、タブを押して移動する案内 */
  moveHint: string | null;
}>;

/**
 * 表示する手順の並び
 * 不変条件: steps の先頭は welcome、末尾は finish（どちらもホーム）。stops は立ち寄る順の画面（ホームは1回だけ）。
 */
export type GuidedTourPlan = Readonly<{ steps: readonly GuidedTourStep[]; stops: readonly GuidedTourStop[] }>;

/** 最後の手順の次へボタンの文言 */
const FINISH_NEXT_LABEL = '⚔️ 冒険をはじめる！';

/** 同じ画面の中で進むときの次へボタンの文言 */
const SAME_ROUTE_NEXT_LABEL = '次へ →';

/**
 * 公開設定から、表示する手順の並びを作る
 * 準備中の画面の手順を除き、帯・進捗・次へボタン・行き先のタブを並びから導出する。
 * @param {PublicAppSettings} settings - 公開アプリ設定
 * @returns {GuidedTourPlan} 手順の並び
 */
export const buildGuidedTourPlan = (settings: PublicAppSettings): GuidedTourPlan => {
  const included = STEP_DEFINITIONS.filter((definition) => definition.include(settings));
  const lastIndex = included.length - 1;
  /**
   * 同じ画面の手順が続く範囲を返す（welcome と finish は帯を別にするため、範囲に含めない）
   * @param {number} index - 手順の位置
   * @returns {{start: number, end: number}} 範囲の先頭と末尾の位置
   */
  const groupOf = (index: number): { start: number; end: number } => {
    let start = index;
    let end = index;
    while (start - 1 > 0 && included[start - 1].route === included[index].route) start -= 1;
    while (end + 1 < lastIndex && included[end + 1].route === included[index].route) end += 1;
    return { start, end };
  };

  const stops: GuidedTourStop[] = [];
  included.forEach((definition) => {
    if (!stops.some((stop) => stop.route === definition.route)) {
      stops.push({ route: definition.route, label: GUIDED_TOUR_STOP_TABS[definition.route].label });
    }
  });

  const steps = included.map((definition, index): GuidedTourStep => {
    const next = included[index + 1] ?? null;
    const changesRoute = next !== null && next.route !== definition.route;
    const nextStopTab = changesRoute ? GUIDED_TOUR_STOP_TABS[next.route] : null;
    const copy = definition.copy(settings);
    const isAnchorVisible = 'anchorVisible' in definition ? definition.anchorVisible(settings) : true;
    const { start, end } = groupOf(index);
    const progress: GuidedTourProgress = index === 0
      ? { kind: 'intro' }
      : index === lastIndex
        ? { kind: 'goal' }
        : { kind: 'stop', stopIndex: stops.findIndex((stop) => stop.route === definition.route) };
    const stopLabel = GUIDED_TOUR_STOP_TABS[definition.route].label;

    return {
      id: definition.id,
      route: definition.route,
      anchor: isAnchorVisible ? definition.anchor : null,
      tapThrough: definition.tapThrough,
      badge: progress.kind === 'intro'
        ? 'WELCOME'
        : progress.kind === 'goal'
          ? 'GOAL'
          : `${stopLabel} ${index - start + 1}/${end - start + 1}`,
      progress,
      title: copy.title,
      body: copy.body,
      hint: copy.hint,
      nextLabel: next === null ? FINISH_NEXT_LABEL : nextStopTab ? `${nextStopTab.label}へ →` : SAME_ROUTE_NEXT_LABEL,
      nextTabAnchor: nextStopTab?.tabAnchor ?? null,
      moveHint: nextStopTab
        ? `下の「${nextStopTab.label}」を押して${next.route === VISITOR_ROUTES.HOME ? '戻ろう' : '移動しよう'}`
        : null,
    };
  });

  return { steps, stops };
};

/** 並びの中の現在位置 */
export type GuidedTourPosition = Readonly<{ step: GuidedTourStep; index: number }>;

/**
 * 手順IDから現在位置を求める
 * 設定変更（準備中への切替など）で手順が並びから消えた場合は、正本の順で次に残っている手順へ進める。
 * finish は常に残るため、必ずどこかに着地する。
 * @param {GuidedTourPlan} plan - 手順の並び
 * @param {GuidedTourStepId} stepId - storeが持つ手順ID
 * @returns {GuidedTourPosition} 現在位置
 */
export const locateGuidedTourStep = (plan: GuidedTourPlan, stepId: GuidedTourStepId): GuidedTourPosition => {
  const directIndex = plan.steps.findIndex((step) => step.id === stepId);
  if (directIndex >= 0) {
    return { step: plan.steps[directIndex], index: directIndex };
  }
  const canonicalOrder: readonly string[] = STEP_DEFINITIONS.map((definition) => definition.id);
  const removedOrder = canonicalOrder.indexOf(stepId);
  const fallbackIndex = plan.steps.findIndex((step) => canonicalOrder.indexOf(step.id) > removedOrder);
  const index = fallbackIndex >= 0 ? fallbackIndex : plan.steps.length - 1;
  return { step: plan.steps[index], index };
};

/** 画面の移動（タブ・ブラウザの戻る・他画面からの遷移）に対するガイドの対応 */
export type GuidedTourRouteDecision =
  /** 今の手順の画面にいる */
  | Readonly<{ kind: 'stay' }>
  /** 別の立ち寄り先へ移った。その画面の手順へ合わせる */
  | Readonly<{ kind: 'jump'; stepId: GuidedTourStepId }>
  /** 立ち寄り先ではない画面にいる。ガイドを一時停止し、戻る導線を出す */
  | Readonly<{ kind: 'detour' }>
  /** ログインし直しの画面にいる。既読にせずガイドを終える */
  | Readonly<{ kind: 'abandon' }>;

/**
 * 表示中の画面とガイドの手順を突き合わせる
 * 画面が変わったときだけ呼ぶ。来場者の移動にガイドが付いていく方針で、先の立ち寄り先へ進んだらその最初の手順（次へと同じ）、
 * 前の立ち寄り先へ戻ったらその最後の手順（戻るボタンと同じ）へ合わせる。
 * 同じ画面が並びに2回出る（ホーム: 最初と finish）場合は、立ち寄り先の数で近い側を選び、同じなら先へ進む側を選ぶ。
 * @param {GuidedTourPlan} plan - 手順の並び
 * @param {GuidedTourStepId} currentStepId - 現在の手順ID
 * @param {string | null} routeName - 表示中のルート名（URLが来場者ルートでなければnull）
 * @returns {GuidedTourRouteDecision} 対応
 */
export const reconcileGuidedTourWithRoute = (
  plan: GuidedTourPlan,
  currentStepId: GuidedTourStepId,
  routeName: string | null,
): GuidedTourRouteDecision => {
  const { step, index } = locateGuidedTourStep(plan, currentStepId);
  if (routeName === step.route) {
    return { kind: 'stay' };
  }
  if (routeName !== null && ABANDON_ROUTES.has(routeName)) {
    return { kind: 'abandon' };
  }

  // 同じ画面が続く範囲を1つの立ち寄りとして番号を振る
  const groupIndexes: number[] = [];
  plan.steps.forEach((planStep, stepIndex) => {
    const previous = plan.steps[stepIndex - 1];
    groupIndexes.push(stepIndex === 0 ? 0 : groupIndexes[stepIndex - 1] + (previous.route === planStep.route ? 0 : 1));
  });
  const currentGroup = groupIndexes[index];
  const candidateGroups = [...new Set(
    plan.steps.flatMap((planStep, stepIndex) => (planStep.route === routeName ? [groupIndexes[stepIndex]] : [])),
  )];
  if (candidateGroups.length === 0) {
    return { kind: 'detour' };
  }

  const targetGroup = candidateGroups.reduce((best, group) => {
    const distance = Math.abs(group - currentGroup);
    const bestDistance = Math.abs(best - currentGroup);
    return distance < bestDistance || (distance === bestDistance && group > best) ? group : best;
  });
  const groupStepIndexes = groupIndexes.flatMap((group, stepIndex) => (group === targetGroup ? [stepIndex] : []));
  const targetIndex = targetGroup > currentGroup ? groupStepIndexes[0] : groupStepIndexes[groupStepIndexes.length - 1];
  return { kind: 'jump', stepId: plan.steps[targetIndex].id };
};
